import React, { useEffect, useState } from "react";

const contraceptiveOptions = [
    {
        value: "Pill, patch or ring",
        label: "Pill, patch or ring",
    },
    {
        value: "Hormonal IUD",
        label: "Hormonal IUD",
    },
    {
        value: "Copper IUD",
        label: "Copper IUD",
    },
    {
        value: "Implant",
        label: "Implant",
    },
    {
        value: "Injection",
        label: "Injection",
    },
    {
        value: "Condoms",
        label: "Condoms",
    },
    {
        value: "Hysterectomy",
        label: "Hysterectomy",
    },
    {
        value: "Tubal ligation/partner vasectomy",
        label: "Tubal ligation/partner vasectomy",
    },
    {
        value: "Other",
        label: "Other",
    },
];

const ContraceptivesQuestion = ({ onSubmit, onNext, initialValue, ensureFullyVisible }) => {
    const [selected, setSelected] = useState([]);
    const [otherText, setOtherText] = useState("");

    // Pre-fill when editing
    useEffect(() => {
        if (initialValue) {
            if (Array.isArray(initialValue)) {
                setSelected(initialValue);
            } else if (typeof initialValue === "object" && initialValue.selections) {
                setSelected(Array.isArray(initialValue.selections) ? initialValue.selections : []);
                setOtherText(initialValue.otherText || "");
            } else if (typeof initialValue === "string") {
                // Legacy format: single string
                setSelected([initialValue]);
            }
        }
    }, [initialValue]);

    const toggle = (value, event) => {
        // Blur the button after click to prevent focus from persisting and looking like selection
        if (event && event.currentTarget) {
            event.currentTarget.blur();
        }
        
        setSelected((prev) => {
            const next = prev.includes(value) 
                ? prev.filter((x) => x !== value) 
                : [...prev, value];
            
            // Clear other text if "Other" is deselected
            if (!next.includes("Other")) {
                setOtherText("");
            }
            
            queueMicrotask(() => ensureFullyVisible?.());
            return next;
        });
    };

    // Also nudge after the state has painted (mobile keyboards/layout shifts)
    useEffect(() => {
        if (selected.length) {
            const t = setTimeout(() => ensureFullyVisible?.(), 120);
            return () => clearTimeout(t);
        }
    }, [selected, ensureFullyVisible]);

    const handleSubmit = (e) => {
        e.preventDefault();
        
        // If nothing selected, submit empty array (represents "None of the above")
        if (selected.length === 0) {
            onSubmit([]);
            onNext();
            return;
        }
        
        // If "Other" is selected, require text input
        if (selected.includes("Other") && !otherText.trim()) {
            return;
        }
        
        // Submit as array if no "Other", or as object if "Other" is selected
        const answer = selected.includes("Other")
            ? { selections: selected, otherText: otherText.trim() }
            : selected;
            
        onSubmit(answer);
        onNext();
        
        // reset
        setSelected([]);
        setOtherText("");
    };

    // Can submit if: nothing selected (None of the above), or selections made and if "Other" is selected, text is provided
    const canSubmit = selected.length === 0 || (selected.length > 0 && (!selected.includes("Other") || otherText.trim().length > 0));

    return (
        <form onSubmit={handleSubmit} className="input-form">
            <div className="treatment-options-container">
                <div className="treatment-options">
                    {contraceptiveOptions.map((opt) => {
                        const isSelected = selected.includes(opt.value);
                        return (
                            <button
                                key={opt.value}
                                type="button"
                                className={`treatment-button ${isSelected ? "treatment-button-selected" : ""}`}
                                onClick={(e) => toggle(opt.value, e)}
                                aria-pressed={isSelected}
                            >
                                {opt.label}
                            </button>
                        );
                    })}
                </div>

                {/* Other text input */}
                {selected.includes("Other") && (
                    <div style={{ marginTop: 12, width: '100%', maxWidth: '100%', padding: '0 0.5rem', boxSizing: 'border-box' }}>
                        <input
                            type="text"
                            className="input-field input-field-small"
                            placeholder="Other contraceptive method"
                            value={otherText}
                            onChange={(e) => setOtherText(e.target.value)}
                            style={{ width: '100%', maxWidth: '100%', boxSizing: 'border-box' }}
                        />
                    </div>
                )}

                <button
                    type="submit"
                    className="treatment-submit-button"
                    disabled={!canSubmit}
                    onFocus={() => ensureFullyVisible?.()}
                >
                    {selected.length > 0 ? "Continue" : "None of the above"}
                </button>
            </div>
        </form>
    );
};

export default ContraceptivesQuestion;
